import React, { useState, useEffect } from 'react';
import { X, AlertOctagon, Save, Clock, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';
import { useTranslation } from 'react-i18next'; 

const ReportDowntimeModal = ({ isOpen, onClose, assetNum, onRefresh }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

    const [formData, setFormData] = useState({
        assetnum: assetNum,
        startdate: new Date().toISOString().slice(0, 16),
        enddate: '', 
        downtimetype: 'UNPLANNED',
        reasoncode: 'BREAKDOWN',
        is_production_loss: true,
        duration: 0
    });

    useEffect(() => {
        if (formData.startdate && formData.enddate) {
            const start = new Date(formData.startdate);
            const end = new Date(formData.enddate);
            const diffHours = (end - start) / (1000 * 60 * 60);
            setFormData(prev => ({ ...prev, duration: diffHours > 0 ? parseFloat(diffHours.toFixed(2)) : 0 }));
        } else {
            setFormData(prev => ({ ...prev, duration: 0 }));
        }
    }, [formData.startdate, formData.enddate]);

    if (!isOpen) return null;

    const handleSave = async () => {
        const loadId = toast.loading(t('downtime.messages.recording', "Recording downtime..."));
        try {
            const payload = {
                assetnum: assetNum,
                startdate: new Date(formData.startdate).toISOString(),
                enddate: formData.enddate ? new Date(formData.enddate).toISOString() : null,
                downtimetype: formData.downtimetype,
                reasoncode: formData.reasoncode,
                is_production_loss: Boolean(formData.is_production_loss),
                duration: parseFloat(formData.duration) || 0.0
            };

            await api.post('/assets/downtime', payload);
            
            toast.success(t('downtime.messages.success', "Downtime recorded successfully"), { id: loadId });
            if (onRefresh) onRefresh();
            onClose();
        } catch (err) {
            if (err.response?.status === 422) {
                toast.error(t('downtime.messages.validation_error', "Format Error: Check all fields"), { id: loadId });
            } else {
                toast.error(t('downtime.messages.server_error', "Server Error: 500"), { id: loadId });
            }
        }
    };

    return (
        <div className={`fixed inset-0 z-[10003] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-in fade-in ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="bg-surface-panel w-[550px] shadow-2xl border-t-4 border-status-danger rounded-sm overflow-hidden flex flex-col">

                {/* Header */}
                <div className={`bg-surface-card p-4 border-b border-line flex justify-between items-center `}>
                    <div className={`flex items-center gap-3 text-ink `}>
                        <div className="bg-status-danger-bg p-2 rounded">
                            <AlertOctagon size={24} className="text-status-danger" />
                        </div>
                        <div>
                            <h2 className="font-black text-sm uppercase tracking-tighter">{t('downtime.title', 'Report Asset Downtime')}</h2>
                            <p className="text-[10px] text-ink-secondary font-bold uppercase tracking-widest">{t('downtime.subtitle', 'Asset Management System')}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-ink-secondary hover:text-ink"><X size={24}/></button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Asset Info Strip */}
                    <div className={`bg-primary-dark text-ink-inverse p-3 flex justify-between items-center rounded-sm shadow-inner `}>
                        <span className="text-[11px] font-bold uppercase tracking-widest">{t('downtime.asset', 'Asset')}: {assetNum}</span>
                        <div className={`flex items-center gap-2 `}>
                             <Activity size={14} className="animate-pulse text-status-danger" />
                             <span className="text-[10px] font-black uppercase bg-status-danger/20 px-2 py-0.5 rounded">{t('downtime.status', 'Offline Recording')}</span>
                        </div>
                    </div>

                    {/* Time Section */}
                    <div className={`grid grid-cols-2 gap-4 ${isRTL ? 'text-right' : ''}`}>
                        <div className="space-y-1">
                            <label className={`text-[9px] font-black text-ink-secondary uppercase flex items-center gap-1 `}>
                                <Clock size={12}/> {t('downtime.start', 'Start Date & Time')}
                            </label>
                            <input
                                type="datetime-local"
                                className={`w-full border-2 border-line p-2 text-xs font-bold focus:border-primary outline-none transition-all ${isRTL ? 'text-right' : ''}`}
                                value={formData.startdate}
                                onChange={(e) => setFormData({...formData, startdate: e.target.value})}
                            />
                        </div>
                        <div className="space-y-1 opacity-80">
                            <label className={`text-[9px] font-black text-ink-secondary uppercase flex items-center gap-1 `}>
                                <Clock size={12}/> {t('downtime.end', 'End Date (Optional)')}
                            </label>
                            <input
                                type="datetime-local"
                                className={`w-full border-2 border-line p-2 text-xs font-bold focus:border-status-success outline-none transition-all ${isRTL ? 'text-right' : ''}`}
                                value={formData.enddate}
                                onChange={(e) => setFormData({...formData, enddate: e.target.value})}
                            />
                        </div>
                    </div>

                    {/* Logic Row */}
                    <div className="grid grid-cols-2 gap-4 bg-surface-card p-4 border border-line rounded-sm">
                         <div className="space-y-1">
                            <label className="text-[9px] font-black text-ink-secondary uppercase">{t('downtime.type', 'Downtime Type')}</label>
                            <select
                                className="w-full border border-line p-2 text-xs font-bold bg-surface-panel outline-none"
                                value={formData.downtimetype}
                                onChange={(e) => setFormData({...formData, downtimetype: e.target.value})}
                            >
                                <option value="UNPLANNED">{t('downtime.types.unplanned', 'UNPLANNED (Breakdown)')}</option>
                                <option value="PLANNED">{t('downtime.types.planned', 'PLANNED (Maintenance)')}</option>
                            </select>
                         </div>
                         <div className="space-y-1">
                            <label className="text-[9px] font-black text-ink-secondary uppercase">{t('downtime.duration', 'Calculated Duration')}</label>
                            <div className="w-full border border-dashed border-line p-2 text-xs font-black text-primary text-center bg-surface-panel">
                                {formData.duration} {t('downtime.hours', 'HOURS')}
                            </div>
                         </div>
                    </div>

                    {/* Production Loss Toggle */}
                    <div className={`flex items-center gap-3 bg-status-danger-bg p-3 border border-status-danger/20 rounded-sm `}>
                        <input
                            type="checkbox"
                            id="prod_loss_toggle"
                            className="w-4 h-4 accent-status-danger cursor-pointer"
                            checked={formData.is_production_loss}
                            onChange={(e) => setFormData({...formData, is_production_loss: e.target.checked})}
                        />
                        <label htmlFor="prod_loss_toggle" className="text-[10px] font-black text-status-danger uppercase tracking-widest cursor-pointer">
                            {t('downtime.prod_impact', 'Operational Downtime (Production Impact)')}
                        </label>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className={`bg-surface-card p-4 border-t border-line flex gap-3 shadow-inner ${isRTL ? 'justify-start' : 'justify-end'}`}>
                    <button onClick={onClose} className="px-6 py-2 text-[10px] font-black uppercase text-ink-secondary hover:text-ink">{t('common.close', 'Close')}</button>
                    <button
                        onClick={handleSave}
                        className={`bg-primary text-ink-inverse px-10 py-2 text-[10px] font-black uppercase flex items-center gap-2 hover:bg-primary-hover transition-all shadow-xl `}
                    >
                        <Save size={14} /> {t('downtime.btn_commit', 'Commit Changes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportDowntimeModal;